<template>
  <div class="product-selection-container">
    <!-- Header Section -->
    <div class="selection-header">
      <div
v-if="settings && settings.logo_url"
class="tw-mb-6 tw-text-center"
>
        <img
:src="settings.logo_url"
alt="Company Logo"
class="tw-h-12 tw-object-contain tw-inline-block"
>
      </div>
      
      <!-- Cart Return Button -->
      <div
v-if="cartItemsCount > 0"
class="cart-status"
>
        <button
class="cart-return-btn"
@click="$emit('view-quote')"
>
          <svg
class="tw-w-5 tw-h-5 tw-mr-2"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
            <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6m0 0v2m0-2h2m-2 0h-2"
/>
          </svg>
          <span>View Quote</span>
          <span class="cart-badge">{{ cartItemsCount }}</span>
        </button>
      </div>
      
      <div class="tw-text-center">
        <h1 class="tw-text-3xl tw-font-light tw-text-gray-800 tw-mb-2">
          Choose Your Product
        </h1>
        <p class="tw-text-gray-500">
          Select a product to customize and add to your quote
        </p>
      </div>
    </div>

    <!-- Products Grid -->
    <div class="products-grid">
      <div
        v-for="product in products"
        :key="product.id"
        class="product-card tw-group"
        @click="selectProduct(product)"
      >
        <!-- Product Image -->
        <div class="product-image-wrapper">
          <img 
            :src="product.image" 
            :alt="product.name" 
            class="product-image"
            @error="handleImageError($event)"
          >
          <div class="product-overlay">
            <span class="tw-text-white tw-font-medium">Customize →</span>
          </div>
        </div>
        
        <!-- Product Info -->
        <div class="product-info">
          <h3 class="product-name">
{{ product.name }}
</h3>
          <div
v-if="product.price"
class="product-price"
>
            Starting at {{ formatPrice(product.price) }}
          </div>
        </div>
        
        <!-- Quick Action -->
        <div class="product-action">
          <button class="select-btn">
            <svg
class="tw-w-4 tw-h-4 tw-mr-1"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
              <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M12 4v16m8-8H4"
/>
            </svg>
            Select
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
v-if="!products || products.length === 0"
class="empty-state"
>
      <svg
class="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-400"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
        <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
/>
      </svg>
      <h3 class="tw-mt-4 tw-text-lg tw-font-medium tw-text-gray-900">
No products available
</h3>
      <p class="tw-mt-2 tw-text-sm tw-text-gray-500">
Please check back later for available products.
</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductSelectionGrid",
  props: {
    products: {
      type: Array,
      required: true,
      default: () => []
    },
    cartItemsCount: {
      type: Number,
      default: 0
    },
    settings: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    selectProduct(product) {
      this.$emit("product-selected", product);
    },
    handleImageError(event) {
      // Fallback to placeholder if image fails to load
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjIwMCIgeT0iMTUwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjE5cHg7Zm9udC1mYW1pbHk6QXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWY7ZG9taW5hbnQtYmFzZWxpbmU6Y2VudHJhbCI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
    },
    formatPrice(price) {
      if (!price) return '';
      if (typeof price === 'string' && price.includes('$')) return price;
      const numPrice = typeof price === 'number' ? price : parseFloat(price);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(numPrice);
    }
  },
};
</script>

<style scoped>
/* Container */
.product-selection-container {
  @apply tw-max-w-7xl tw-mx-auto tw-px-4 tw-py-8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Header Section */
.selection-header {
  @apply tw-mb-12 tw-relative;
}

/* Cart Status */
.cart-status {
  @apply tw-absolute tw-top-0 tw-right-0;
}

.cart-return-btn {
  @apply tw-flex tw-items-center tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300;
  @apply tw-rounded-lg tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200;
  @apply tw-text-gray-700 hover:tw-text-gray-900 tw-relative;
}

.cart-badge {
  @apply tw-ml-2 tw-bg-green-500 tw-text-white tw-text-xs tw-font-bold;
  @apply tw-px-2 tw-py-1 tw-rounded-full tw-min-w-[20px] tw-text-center;
}

/* Products Grid */
.products-grid {
  @apply tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6;
}

/* Product Card */
.product-card {
  @apply tw-bg-white tw-rounded-xl tw-overflow-hidden tw-cursor-pointer;
  @apply tw-border tw-border-gray-200 tw-transition-all tw-duration-300;
  @apply hover:tw-shadow-xl hover:tw-scale-105;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.product-card:hover {
  border-color: #e5e7eb;
}

/* Product Image */
.product-image-wrapper {
  @apply tw-relative tw-h-56 tw-overflow-hidden;
}

.product-image {
  @apply tw-w-full tw-h-full tw-object-contain tw-p-4;
  @apply tw-transition-transform tw-duration-300 group-hover:tw-scale-110;
}

.product-overlay {
  @apply tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-0 group-hover:tw-bg-opacity-60;
  @apply tw-flex tw-items-center tw-justify-center;
  @apply tw-transition-all tw-duration-300 tw-opacity-0 group-hover:tw-opacity-100;
}

/* Product Info */
.product-info {
  @apply tw-p-4;
}

.product-name {
  @apply tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-1;
  @apply tw-break-words;
}

.product-type {
  @apply tw-text-sm tw-text-gray-500 tw-mb-2;
}

.product-price {
  @apply tw-text-sm tw-font-medium tw-text-green-600 tw-mt-2;
}

/* Product Action */
.product-action {
  @apply tw-px-4 tw-pb-4;
}

.select-btn {
  @apply tw-w-full tw-flex tw-items-center tw-justify-center;
  @apply tw-px-4 tw-py-2 tw-bg-gray-900 tw-text-white;
  @apply tw-rounded-lg tw-font-medium tw-text-sm;
  @apply tw-transition-all tw-duration-200;
  @apply hover:tw-bg-gray-800 active:tw-scale-95;
}

/* Empty State */
.empty-state {
  @apply tw-text-center tw-py-12;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .product-selection-container {
    @apply tw-px-2 tw-py-4;
  }
  
  .selection-header {
    @apply tw-mb-8;
  }
  
  .cart-status {
    @apply tw-static tw-mb-4 tw-flex tw-justify-center;
  }
  
  .products-grid {
    @apply tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4;
  }
  
  .product-image-wrapper {
    @apply tw-h-48;
  }
}

@media (max-width: 480px) {
  .product-card:hover {
    @apply tw-scale-100; /* Disable scale on mobile */
  }
  
  .selection-header h1 {
    @apply tw-text-2xl;
  }
}

/* Loading Animation */
@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

.loading-shimmer {
  animation: shimmer 2s infinite;
  background: linear-gradient(
    to right,
    #f6f7f8 0%,
    #edeef1 20%,
    #f6f7f8 40%,
    #f6f7f8 100%
  );
  background-size: 1000px 100%;
}

/* Print styles */
@media print {
  .cart-status,
  .product-overlay,
  .select-btn {
    display: none !important;
  }
  
  .product-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
